import { useState, useEffect } from 'react';
import { globalStore } from '../utils/globalStore';
import { PermissionManager } from '../utils/menuPermission';

/**
 * 通用权限管理Hook
 * 用于在组件中检查各种权限
 */
export const usePermission = () => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // 检查是否已初始化
    const checkInitialized = () => {
      setIsInitialized(globalStore.isInitialized());
    };

    // 初始检查
    checkInitialized();

    // 订阅状态变化
    const unsubscribe = globalStore.subscribe(checkInitialized);

    return unsubscribe;
  }, []);

  // 检查是否具有指定权限
  const hasPermission = (permission: string): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.hasPermission(permission);
  };

  // 检查是否具有多个权限中的任意一个
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.hasAnyPermission(permissions);
  };

  // 检查是否具有所有指定权限
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.hasAllPermissions(permissions);
  };

  // 检查是否具有指定权限组中的任意一个权限
  const hasAnyPermissionFromGroup = (permissionGroup: Record<string, string>): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.hasAnyPermissionFromGroup(permissionGroup);
  };

  // 检查是否具有指定权限组中的所有权限
  const hasAllPermissionsFromGroup = (permissionGroup: Record<string, string>): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.hasAllPermissionsFromGroup(permissionGroup);
  };

  // 获取用户具有的权限列表（从指定权限组中筛选）
  const getGrantedPermissionsFromGroup = (permissionGroup: Record<string, string>): string[] => {
    if (!isInitialized) return [];
    return PermissionManager.getGrantedPermissionsFromGroup(permissionGroup);
  };

  // 获取所有权限
  const getAllPermissions = (): string[] => {
    if (!isInitialized) return [];
    return PermissionManager.getAllPermissions();
  };

  // 角色检查
  const isAdmin = (): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.isAdmin();
  };

  const isAssessor = (): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.isAssessor();
  };

  const isOperator = (): boolean => {
    if (!isInitialized) return false;
    return PermissionManager.isOperator();
  };

  // 常用功能权限检查
  const canJoinTeam = (): boolean => {
    return hasPermission('operator:addGroup');
  };

  const canCreateEpisode = (): boolean => {
    return hasPermission('content:write');
  };

  const canEditEpisode = (): boolean => {
    return hasPermission('content:write');
  };

  const canDeleteEpisode = (): boolean => {
    return hasPermission('content:delete');
  };

  const canApproveContent = (): boolean => {
    return hasPermission('content:approve');
  };

  const canRejectContent = (): boolean => {
    return hasPermission('content:reject');
  };

  const canViewUsers = (): boolean => {
    return hasPermission('user:read');
  };

  const canManageUsers = (): boolean => {
    return hasPermission('user:manage');
  };

  const canAccessSystemSettings = (): boolean => {
    return hasPermission('system:settings');
  };

  const canViewLogs = (): boolean => {
    return hasPermission('system:logs');
  };

  return {
    // 基础状态
    isInitialized,
    
    // 基础权限检查
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAnyPermissionFromGroup,
    hasAllPermissionsFromGroup,
    getGrantedPermissionsFromGroup,
    getAllPermissions,
    
    // 角色检查
    isAdmin,
    isAssessor,
    isOperator,
    
    // 功能权限检查
    canJoinTeam,
    canCreateEpisode,
    canEditEpisode,
    canDeleteEpisode,
    canApproveContent,
    canRejectContent,
    canViewUsers,
    canManageUsers,
    canAccessSystemSettings,
    canViewLogs,
    
  };
};
